/**
 * `generate(template, seed) -> instancia`.
 *
 * REGRA QUE PRESERVA A SEMANTICA: uma seed rejeitada NAO vira `seed + 1`.
 * Se o gerador tentasse a proxima seed em silencio, `generate` deixaria de ser uma
 * funcao da seed, e a reprodutibilidade -- que e a premissa psicometrica inteira --
 * cairia. Rejeicao e um resultado observavel de primeira classe.
 *
 * Quem varre o espaco de seeds e quem precisa de uma variante: o percurso sorteia seeds
 * ate uma ser aceita (`primeiraAceita`, em `app/config.ts`), e a variante fixada pelo
 * professor guarda a SEED ACEITA como identidade. Assim ela e sempre re-servivel por
 * si mesma, e a logica de rejeicao nunca precisa rodar na hora de aplicar a prova.
 */

import { hashOf } from '../core/hash.ts';
import { roundTo } from '../core/num.ts';
import type { Rng, Seed } from '../core/rng.ts';
import { rngForTemplate, seedSequence } from '../core/rng.ts';
import { SamplingFailure } from '../core/sampling.ts';
import { envelope, pisoDeDificuldade } from '../schema/difficulty.ts';
import type { ItemInstance, ItemTemplate, ResolvedDistractor, SolveCtx } from '../schema/item.ts';
import { levelOf, stripKey } from '../schema/item.ts';
import { checkAlternatives } from './alternativeRules.ts';
import type { RejectionReason } from './distractors.ts';
import { resolveDistractors } from './distractors.ts';

export type GenerateResult =
  | { readonly status: 'ok'; readonly instance: ItemInstance }
  | {
      readonly status: 'rejected';
      readonly reason: RejectionReason;
      readonly detail: string;
    };

export function generate<P, A>(template: ItemTemplate<P, A>, seed: Seed): GenerateResult {
  const rng: Rng = rngForTemplate(template.id as string, template.version, seed);

  // 1. parametros
  //
  // Só `SamplingFailure` é rejeição de amostragem. Qualquer outra exceção é DEFEITO do
  // gerador — um índice fora da lista, um campo indefinido — e sai como 'build-error',
  // que a bateria não tolera: antes, um TypeError em `params` virava "rejeição" e se
  // escondia dentro do orçamento de 20%.
  let params: P;
  try {
    params = template.params(rng.fork('params'));
  } catch (e) {
    if (e instanceof SamplingFailure) {
      return { status: 'rejected', reason: 'sampling-failure', detail: e.message };
    }
    return { status: 'rejected', reason: 'build-error', detail: `params falhou: ${String(e)}` };
  }

  const ctx: SolveCtx = { rng: rng.fork('solve'), round: roundTo };

  // 2. a chave
  let key: A;
  try {
    key = template.solve(params, ctx);
  } catch (e) {
    return { status: 'rejected', reason: 'build-error', detail: `solve falhou: ${String(e)}` };
  }

  // 3. distratores por variante defeituosa.
  //    Itens de CONSTRUÇÃO não têm distratores: as âncoras de erro do N3/N4 são os
  //    `diagnostics` do alvo, avaliados sobre a construção do aluno.
  let distractors: readonly ResolvedDistractor<A>[] = [];
  if (template.buggy.length > 0) {
    let dist;
    try {
      dist = resolveDistractors(params, key, template.buggy, ctx, {
        need: (template.alternativas ?? 5) - 1,
        ...(template.equals ? { eq: template.equals } : {}),
        ...(template.distractorMinRelSep !== undefined
          ? { minRelSep: template.distractorMinRelSep }
          : {}),
      });
    } catch (e) {
      return {
        status: 'rejected',
        reason: 'build-error',
        detail: `distrator falhou: ${String(e)}`,
      };
    }
    if (dist.status === 'rejected') {
      return { status: 'rejected', reason: dist.reason, detail: dist.detail };
    }
    distractors = dist.distractors;
  }

  // 4. montagem
  let built;
  try {
    built = template.build(params, key, distractors, rng.fork('build'));
  } catch (e) {
    return { status: 'rejected', reason: 'build-error', detail: `build falhou: ${String(e)}` };
  }

  // 5. invariantes das alternativas (as 9 regras do catalogo)
  if (built.interaction.kind === 'select-one') {
    const rep = checkAlternatives(built.interaction);
    if (rep.violations.length > 0) {
      return {
        status: 'rejected',
        reason: 'invariant-violation',
        detail: rep.violations.join('; '),
      };
    }
  }

  // 6. validacao extra do proprio template, e 7. envelope de dificuldade.
  //    Sem proteção, uma exceção aqui não virava rejeição: derrubava quem chamou — o
  //    percurso inteiro, na tela do aluno.
  let difficulty;
  try {
    const extra = template.validate?.(built, params);
    if (extra) {
      return { status: 'rejected', reason: 'invariant-violation', detail: extra };
    }
    const variantSources = template.variantDifficulty(params, key);
    // O piso é DERIVADO do eixo cognitivo e do ano da habilidade, não escolhido pelo
    // template. Um `difficultyBase` explícito continua possível, mas só como exceção
    // justificada — o padrão é a regra valer para o banco inteiro.
    const base = template.difficultyBase ?? pisoDeDificuldade(template.axis, template.skills);
    difficulty = envelope(base, template.difficultySources, variantSources, template.responseModel);
  } catch (e) {
    return { status: 'rejected', reason: 'build-error', detail: `validação falhou: ${String(e)}` };
  }

  const semHash = {
    templateId: template.id,
    templateVersion: template.version,
    seed,
    instanceHash: '',
    level: levelOf({ interaction: built.interaction, stimuli: built.stimuli }),
    taskModel: template.taskModel,
    skills: template.skills,
    axis: template.axis,
    title: template.title,
    stem: built.stem,
    comando: built.comando ?? [],
    stimuli: built.stimuli,
    interaction: built.interaction,
    difficulty,
    rationale: built.rationale,
    ...(built.dicas && built.dicas.length > 0 ? { dicas: built.dicas } : {}),
    generatedAt: '',
  } satisfies ItemInstance;

  // O hash e da versao SEM CHAVE e SEM o carimbo de tempo: e um recibo da instancia
  // que o aluno ve, nao do momento em que ela foi gerada.
  const instanceHash = hashOf(stripKey({ ...semHash, generatedAt: '' }));

  return {
    status: 'ok',
    instance: { ...semHash, instanceHash, generatedAt: new Date().toISOString() },
  };
}

/** Primeira seed aceita a partir de um prefixo. Util em demonstracao e testes. */
export function firstAccepted<P, A>(
  template: ItemTemplate<P, A>,
  prefix = 's',
  limit = 500,
): ItemInstance | null {
  for (const s of seedSequence(prefix, 0, limit)) {
    const r = generate(template, s);
    if (r.status === 'ok') return r.instance;
  }
  return null;
}
